import React, { useMemo, useState, useEffect, useRef } from "react";
import styles from "./WeeklyLoadChart.module.css";
import { startOfWeek, formatWeekLabel } from "../utils/date";
import { getCssVar } from "../utils/theme";

function localKey(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function fmtKg(kg) {
  if (!kg || kg === 0) return "";
  if (kg >= 1000) return `${(kg / 1000).toFixed(1).replace(".", ",")}t`;
  return `${Math.round(kg)}kg`;
}

// Semanas que entram na linha de tendência. Semana sem treino conta como zero
// (antes era ignorada, e a reta continuava subindo mesmo sem treinar), a
// partir da primeira semana com treino. A semana atual, ainda em andamento,
// só entra se já tem treino ou a partir de quinta: sem isso a reta despencaria
// toda segunda de manhã.
export const CURRENT_WEEK_MIN_DAYS = 3; // seg=0 ... qui=3

export function trendWeekIndexes(data, today = new Date()) {
  const first = data.findIndex((d) => d.kg > 0);
  if (first < 0) return [];
  const lastIdx = data.length - 1;
  const current = data[lastIdx];
  const day = new Date(today);
  day.setHours(0, 0, 0, 0);
  const elapsed = Math.round((day - new Date(current.date).setHours(0, 0, 0, 0)) / 86400000);
  const includeCurrent = current.kg > 0 || elapsed >= CURRENT_WEEK_MIN_DAYS;
  const end = includeCurrent ? lastIdx : lastIdx - 1;
  const idx = [];
  for (let i = first; i <= end; i++) idx.push(i);
  return idx;
}

export default function WeeklyLoadChart({ volumeSeries = null, weeks = 8 }) {
  const data = useMemo(() => {
    const base    = startOfWeek(new Date());
    const buckets = [];
    for (let i = weeks - 1; i >= 0; i--) {
      const wk = new Date(base);
      wk.setDate(base.getDate() - i * 7);
      buckets.push({ key: localKey(wk), date: new Date(wk), kg: 0, cardio: 0 });
    }
    const byKey = Object.fromEntries(buckets.map((b) => [b.key, b]));
    if (Array.isArray(volumeSeries)) {
      for (const s of volumeSeries) {
        const key = s.date || s.key;
        if (byKey[key]) {
          byKey[key].kg = Number(s.kg || 0);
          byKey[key].cardio = Number(s.cardio || 0);
        }
      }
    }
    return buckets;
  }, [volumeSeries, weeks]);

  // Índice da última e penúltima semana com dados — para colorir a barra final
  const lastIdx = useMemo(() => { for (let i=data.length-1;i>=0;i--) if(data[i].kg>0) return i; return -1; }, [data]);
  const prevIdx = useMemo(() => { for (let i=lastIdx-1;i>=0;i--) if(data[i].kg>0) return i; return -1; }, [data, lastIdx]);
  const delta   = lastIdx >= 0 && prevIdx >= 0 ? data[lastIdx].kg - data[prevIdx].kg : null;
  const maxKg   = Math.max(1, ...data.map((d) => d.kg));

  const trendIdx = useMemo(() => new Set(trendWeekIndexes(data)), [data]);

  const [animatedHeights, setAnimatedHeights] = useState(() => data.map(() => 0));
  const [activeIdx, setActiveIdx]             = useState(null);
  const [scrollable, setScrollable]           = useState(false);
  const [trendPath, setTrendPath]             = useState("");
  const [svgViewBox, setSvgViewBox]           = useState("0 0 0 0");

  const chartRef    = useRef(null);
  const barWrapRefs = useRef([]);

  // Animação das barras — duplo rAF
  useEffect(() => {
    const targets = data.map((d) => (d.kg / maxKg) * 100);
    let id1, id2;
    id1 = requestAnimationFrame(() => { id2 = requestAnimationFrame(() => setAnimatedHeights(targets)); });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [data, maxKg]);

  // Scroll para o fim (semanas mais recentes)
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
    setScrollable(el.scrollWidth > el.clientWidth + 4);
  }, [data]);

  useEffect(() => {
    const check = () => chartRef.current && setScrollable(chartRef.current.scrollWidth > chartRef.current.clientWidth + 4);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Linha de tendência — calculada após a transição CSS terminar
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = chartRef.current;
      if (!el) return;
      const chartRect = el.getBoundingClientRect();
      const fullWidth = el.scrollWidth;
      setSvgViewBox(`0 0 ${fullWidth} ${chartRect.height}`);
      setScrollable(el.scrollWidth > el.clientWidth + 4);

      const scrollLeft = el.scrollLeft;
      const points = [];
      barWrapRefs.current.forEach((wrap, i) => {
        if (!wrap || !trendIdx.has(i)) return;
        const wRect = wrap.getBoundingClientRect();
        const barEl = wrap.querySelector("[data-bar]");
        if (!barEl) return;
        const bRect = barEl.getBoundingClientRect();
        const cx = wRect.left - chartRect.left + scrollLeft + wRect.width / 2;
        const cy = bRect.top  - chartRect.top;
        points.push({ cx, cy });
      });

      if (points.length < 2) { setTrendPath(""); return; }

      // Regressão linear — reta de tendência geral
      const n   = points.length;
      const sumX = points.reduce((s, p) => s + p.cx, 0);
      const sumY = points.reduce((s, p) => s + p.cy, 0);
      const sumXY = points.reduce((s, p) => s + p.cx * p.cy, 0);
      const sumX2 = points.reduce((s, p) => s + p.cx * p.cx, 0);
      const denom = n * sumX2 - sumX * sumX;
      if (Math.abs(denom) < 0.001) { setTrendPath(""); return; }
      const slope = (n * sumXY - sumX * sumY) / denom;
      const intercept = (sumY - slope * sumX) / n;
      const x0 = points[0].cx;
      const x1 = points[n - 1].cx;
      const y0 = slope * x0 + intercept;
      const y1 = slope * x1 + intercept;
      setTrendPath(`M ${x0.toFixed(1)} ${y0.toFixed(1)} L ${x1.toFixed(1)} ${y1.toFixed(1)}`);
    }, 260);
    return () => clearTimeout(timer);
  }, [animatedHeights, data, trendIdx]);

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>Progressão de carga</div>
          <div className={styles.sub}>
            Kg movimentados por semana
            {delta !== null && (
              <span className={delta >= 0 ? styles.deltaUp : styles.deltaDown}>
                {delta >= 0 ? " ↑" : " ↓"} {fmtKg(Math.abs(delta))} vs sem. ant.
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.chartWrap}>
        <div className={styles.chart} ref={chartRef}>
          {data.map((d, idx) => {
            const pct      = (d.kg / maxKg) * 100;
            const isLast   = idx === lastIdx;
            const isActive = idx === activeIdx;
            const cardioOnly = d.kg === 0 && d.cardio > 0;
            const barClass = [
              styles.bar,
              d.kg > 0 ? styles.barNonZero : "",
              isLast && delta !== null ? (delta >= 0 ? styles.barUp : styles.barDown) : "",
            ].filter(Boolean).join(" ");

            return (
              <div key={d.key} className={styles.col}>
                <div
                  ref={(el) => { barWrapRefs.current[idx] = el; }}
                  className={styles.barWrap}
                  role="button" tabIndex={0}
                  aria-label={`Semana ${formatWeekLabel(d.date)}: ${fmtKg(d.kg) || (cardioOnly ? "só cardio" : "sem dados")}`}
                  onClick={() => setActiveIdx(isActive ? null : idx)}
                  onKeyDown={(e) => { if (e.key==="Enter"||e.key===" ") setActiveIdx(isActive?null:idx); }}
                >
                  {d.kg > 0 && <span className={styles.valueTag}>{fmtKg(d.kg)}</span>}
                  {/* Semana só de cardio: não tem carga (peso x reps), mas houve
                      treino; sem o rótulo a barra vazia parecia "não treinei". */}
                  {cardioOnly && <span className={styles.cardioTag}>só cardio</span>}
                  <div
                    data-bar
                    className={barClass}
                    style={{ height: `${Math.max(0, Math.min(pct, animatedHeights[idx] ?? pct))}%` }}
                  />
                  <div className={[
                    styles.tooltip,
                    isActive ? styles.tooltipVisible : "",
                    idx === 0 ? styles.tooltipLeft : "",
                    idx === data.length-1 ? styles.tooltipRight : "",
                  ].filter(Boolean).join(" ")}>
                    {d.kg > 0 ? `${fmtKg(d.kg)} movimentados` : cardioOnly ? "Só cardio nesta semana" : "Sem dados"}
                  </div>
                </div>
                <div className={styles.label}>{formatWeekLabel(d.date)}</div>
              </div>
            );
          })}

          {/* Linha de tendência — rola junto com as barras */}
          {trendPath && (
            <svg
              className={styles.trendSvg}
              viewBox={svgViewBox}
              style={{ width: `${svgViewBox.split(" ")[2]}px` }}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <filter id="loadGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              {/* Halo suave */}
              <path d={trendPath} fill="none"
                stroke={`rgba(${getCssVar("--accent-rgb","29,158,117")},0.12)`}
                strokeWidth="8" strokeLinecap="round" />
              {/* Reta de tendência */}
              <path d={trendPath} fill="none"
                stroke={getCssVar("--blue","#1d9e75")}
                strokeWidth="2" strokeLinecap="round"
                filter="url(#loadGlow)" />
            </svg>
          )}
        </div>
        {scrollable && <div className={styles.scrollFade} aria-hidden="true" />}
      </div>
      {scrollable && <div className={styles.scrollHint}>← arraste para ver semanas anteriores</div>}
    </div>
  );
}
